import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import RoleProtectedRoute from "@/components/RoleProtectedRoute";
import { AnalyticsPageTracker } from "@/components/AnalyticsPageTracker";
import FeedbackWidget from "@/components/FeedbackWidget";
import Index from "./pages/Index";

// Lazy-loaded routes for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminGuide = lazy(() => import("./pages/AdminGuide"));
const AdminWizard = lazy(() => import("./pages/AdminWizard"));
const OperatorWizard = lazy(() => import("./pages/OperatorWizard"));
const ViewerGuide = lazy(() => import("./pages/ViewerGuide"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Settings = lazy(() => import("./pages/Settings"));
const Inventory = lazy(() => import("./pages/Inventory"));
const SyncHistory = lazy(() => import("./pages/SyncHistory"));
const UserRoles = lazy(() => import("./pages/UserRoles"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OrgSettings = lazy(() => import("./pages/OrgSettings"));
const CveBrowser = lazy(() => import("./pages/CveBrowser"));
const PortLookup = lazy(() => import("./pages/PortLookup"));
const ComplianceBrowser = lazy(() => import("./pages/ComplianceBrowser"));
const KbArticles = lazy(() => import("./pages/KbArticles"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const NetworkTopology = lazy(() => import("./pages/NetworkTopology"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Demo = lazy(() => import("./pages/Demo"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnalyticsPageTracker />
          <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/setup-org" element={<Onboarding />} />
              <Route path="/dashboard" element={
                <RoleProtectedRoute requiredRoles={["admin", "operator", "viewer"]}>
                  <Dashboard />
                </RoleProtectedRoute>
              } />
              <Route path="/admin" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <Admin />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/guide" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <AdminGuide />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/wizard" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <AdminWizard />
                </RoleProtectedRoute>
              } />
              <Route path="/operator/wizard" element={
                <RoleProtectedRoute requiredRoles={["admin", "operator"]}>
                  <OperatorWizard />
                </RoleProtectedRoute>
              } />
              <Route path="/viewer/guide" element={
                <RoleProtectedRoute requiredRoles={["admin", "operator", "viewer"]}>
                  <ViewerGuide />
                </RoleProtectedRoute>
              } />
              <Route path="/settings" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <Settings />
                </RoleProtectedRoute>
              } />
              <Route path="/inventory" element={
                <RoleProtectedRoute requiredRoles={["admin", "operator"]}>
                  <Inventory />
                </RoleProtectedRoute>
              } />
              <Route path="/sync-history" element={
                <RoleProtectedRoute requiredRoles={["admin", "operator"]}>
                  <SyncHistory />
                </RoleProtectedRoute>
              } />
              <Route path="/user-roles" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <UserRoles />
                </RoleProtectedRoute>
              } />
              <Route path="/org-settings" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <OrgSettings />
                </RoleProtectedRoute>
              } />
              <Route path="/cves" element={<CveBrowser />} />
              <Route path="/ports" element={<PortLookup />} />
              <Route path="/compliance" element={<ComplianceBrowser />} />
              <Route path="/kb-articles" element={<KbArticles />} />
              <Route path="/audit-logs" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <AuditLogs />
                </RoleProtectedRoute>
              } />
              <Route path="/topology" element={
                <RoleProtectedRoute requiredRoles={["admin", "operator"]}>
                  <NetworkTopology />
                </RoleProtectedRoute>
              } />
              <Route path="/analytics" element={
                <RoleProtectedRoute requiredRoles={["admin"]}>
                  <Analytics />
                </RoleProtectedRoute>
              } />
              <Route path="/invite" element={<InviteAccept />} />
              <Route path="/api-docs" element={<ApiDocs />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <FeedbackWidget />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
